import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { useDocsTree } from '../api/use-docs.query';
import { countDocs, flattenDocs, searchDocs } from '../lib/docsNavigation';

/** 검색어가 비었을 때 그냥 보여줄 문서 수. 빈 화면보다 낫다. */
const IDLE_SUGGESTIONS = 6;

const OPTION_ID = (index: number) => `docs-search-option-${index}`;

/**
 * 문서 검색(⌘K).
 *
 * **열려 있을 때만 마운트한다.** 닫혀 있어도 남아 있으면 검색어·커서를 effect 로
 * 되돌려야 하는데, 그게 곧 렌더 연쇄다. 언마운트가 초기화다.
 *
 * 접근성은 combobox 패턴을 따른다 — 포커스는 입력창에 머무르고 ↑↓ 는
 * `aria-activedescendant` 로 "지금 어떤 항목인지"를 보조기기에 알린다. 시각적
 * 하이라이트만 움직이면 화면낭독기 사용자는 어디로 가는지 모른 채 Enter 를 누른다.
 *
 * 인덱스는 트리에서 바로 만든다 — 서버 왕복도, 검색 라이브러리도 없다.
 * 본문(heading)까지 훑으려면 서버 전문검색이 필요하고 그건 별도 작업이다.
 */
export function DocsSearchDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { data: tree, isPending } = useDocsTree();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);

  const results = useMemo(
    () =>
      query.trim()
        ? searchDocs(tree, query)
        : flattenDocs(tree).slice(0, IDLE_SUGGESTIONS),
    [tree, query],
  );

  // 포커스 이동은 상태가 아니라 DOM 조작이라 effect 가 맞는 자리다.
  useEffect(() => inputRef.current?.focus(), []);

  // ESC 는 포커스와 무관하게 닫는다. 다이얼로그 빈 곳이나 헤더를 누르면 포커스가
  // body 로 빠져서, 다이얼로그의 onKeyDown 만으로는 ESC 가 먹지 않았다.
  useEffect(() => {
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [onClose]);

  // 목록은 6행 남짓만 보인다. 하이라이트가 화면 밖으로 나가면 사용자가 보는 행과
  // 실제 선택이 어긋난다.
  useEffect(() => {
    listRef.current
      ?.querySelector(`#${CSS.escape(OPTION_ID(cursor))}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const go = (index: number) => {
    const hit = results[index];
    if (!hit) return;
    onClose();
    navigate(hit.doc.path);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    // 전역 ⌘K 핸들러는 입력 필드에서 오는 이벤트를 무시한다(문의 폼 등에서
    // 가로채면 안 되니까). 그래서 이 다이얼로그의 입력창에서는 여기서 닫는다.
    if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onClose();
      return;
    }
    // 이 다이얼로그의 조작 대상은 입력창 하나뿐이다. Tab 을 흘려보내면 포커스가
    // 뒤 페이지로 빠지면서 aria-modal 이 거짓말이 된다.
    if (event.key === 'Tab') {
      event.preventDefault();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((prev) => Math.min(prev + 1, results.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      go(cursor);
    }
  };

  // body 로 띄운다. 첫 화면은 MainLayout 의 main(z-10) 안이라 그 자리에 그리면
  // 고정 헤더(z-50)가 배경 위로 올라와서, 헤더를 누르면 포커스가 빠졌다.
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-center px-4 pt-20">
      {/* 배경은 클릭해 닫기만 한다. 포커스를 받으면 Shift+Tab 이 보이지 않는
          버튼에 걸려서 사용자가 갇힌 것처럼 느낀다. */}
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-neutral-900/40 backdrop-blur-[1.5px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="도움말 문서 검색"
        onKeyDown={onKeyDown}
        className="relative h-fit w-full max-w-160 overflow-hidden rounded-2xl bg-white shadow-modal dark:bg-dark-card"
      >
        <div className="flex items-center gap-3 border-b border-neutral-200 px-4.5 py-4 dark:border-white/12">
          <span aria-hidden className="text-neutral-400">
            ⌕
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setCursor(0);
            }}
            aria-label="도움말 문서 검색어"
            role="combobox"
            aria-expanded
            aria-controls="docs-search-results"
            aria-activedescendant={results[cursor] ? OPTION_ID(cursor) : undefined}
            aria-autocomplete="list"
            placeholder="무엇이 궁금하신가요?"
            className="w-full bg-transparent text-base text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-neutral-100"
          />
          <kbd className="rounded border border-neutral-200 px-1.5 py-0.5 font-mono text-[11px] text-neutral-500 dark:border-white/15">
            ESC
          </kbd>
        </div>

        {isPending ? (
          // 트리가 오기 전에 "결과 없음"을 띄우면 사용자는 문서가 없다고 믿고 나간다.
          <div className="space-y-2.5 px-4.5 py-5" aria-hidden>
            <div className="h-4 w-2/5 rounded bg-neutral-100 dark:bg-white/8" />
            <div className="h-4 w-3/5 rounded bg-neutral-100 dark:bg-white/8" />
            <div className="h-4 w-1/2 rounded bg-neutral-100 dark:bg-white/8" />
          </div>
        ) : results.length === 0 ? (
          <p className="px-4.5 py-8 text-center text-sm text-neutral-500">
            검색 결과가 없습니다. 1:1 문의로 물어봐 주세요.
          </p>
        ) : (
          <ul
            ref={listRef}
            id="docs-search-results"
            role="listbox"
            aria-label="검색 결과"
            className="max-h-96 overflow-y-auto py-1.5"
          >
            {results.map((hit, index) => (
              <li key={hit.doc.id} role="presentation">
                <button
                  type="button"
                  id={OPTION_ID(index)}
                  role="option"
                  aria-selected={index === cursor}
                  // 조작은 입력창의 ↑↓ 가 맡는다. Tab 순서에서는 빼서
                  // 포커스가 목록 안에서 길을 잃지 않게 한다.
                  tabIndex={-1}
                  onMouseEnter={() => setCursor(index)}
                  onClick={() => go(index)}
                  className={[
                    'grid w-full grid-cols-[1fr_auto] items-center gap-3 px-4.5 py-2.5 text-left',
                    index === cursor
                      ? 'bg-brand-50 shadow-[inset_2px_0_0_var(--color-brand-500)] dark:bg-brand-500/12'
                      : '',
                  ].join(' ')}
                >
                  <span>
                    <span className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {hit.doc.title}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-neutral-400">
                      {hit.doc.summary}
                    </span>
                  </span>
                  <span className="font-mono text-[11px] text-neutral-300 dark:text-white/25">
                    {hit.section.slug}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-4 border-t border-neutral-200 bg-neutral-50 px-4.5 py-2.5 text-[11.5px] text-neutral-400 dark:border-white/12 dark:bg-white/5">
          <span>↑ ↓ 이동</span>
          <span>↵ 열기</span>
          <span className="ml-auto">
            {isPending ? '불러오는 중…' : `문서 ${countDocs(tree)}개`}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
